if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        if (this.closest('.nutra-pdp')) this.initGalleryMediaNav();
      }

      initGalleryMediaNav() {
        if (this.dataset.nutraNavBound) return;
        this.dataset.nutraNavBound = 'true';

        this.addEventListener(
          'click',
          (event) => {
            const button = event.target.closest('button.slider-button');
            if (!button || !this.contains(button)) return;
            const name = button.getAttribute('name');
            if (name !== 'previous' && name !== 'next') return;
            event.preventDefault();
            event.stopImmediatePropagation();
            this.stepActiveMedia(name === 'next' ? 1 : -1);
          },
          true
        );

        const patchSlider = (slider) => {
          if (!slider || typeof slider.update !== 'function') return;
          if (slider.dataset.nutraNavPatched) {
            slider.querySelectorAll('button.slider-button').forEach((button) => button.removeAttribute('disabled'));
            return;
          }
          slider.dataset.nutraNavPatched = 'true';
          const originalUpdate = slider.update.bind(slider);
          slider.update = () => {
            originalUpdate();
            slider.querySelectorAll('button.slider-button').forEach((button) => button.removeAttribute('disabled'));
          };
          slider.querySelectorAll('button.slider-button').forEach((button) => button.removeAttribute('disabled'));
        };

        const patchAll = () => {
          patchSlider(this.elements.viewer);
          patchSlider(this.elements.thumbnails);
          this.querySelectorAll('button.slider-button').forEach((button) => button.removeAttribute('disabled'));
        };

        patchAll();
        if (!customElements.get('slider-component')) {
          customElements.whenDefined('slider-component').then(patchAll);
        }

        this.galleryNavObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            const button = mutation.target;
            if (button.matches?.('button.slider-button[disabled]')) {
              button.removeAttribute('disabled');
            }
          });
        });
        this.galleryNavObserver.observe(this, {
          subtree: true,
          attributes: true,
          attributeFilter: ['disabled'],
        });
      }

      getGalleryItems() {
        const thumbs = Array.from(this.elements.thumbnails?.querySelectorAll('[data-target]') || []).filter(
          (item) => !item.hasAttribute('hidden')
        );
        if (thumbs.length > 1) return thumbs;

        return Array.from(this.elements.viewer.querySelectorAll('[data-media-id]')).filter(
          (item) => !item.hasAttribute('hidden')
        );
      }

      stepActiveMedia(direction) {
        const items = this.getGalleryItems();
        if (items.length < 2) return;

        const activeMedia = this.elements.viewer.querySelector('[data-media-id].is-active');
        const activeThumb = this.elements.thumbnails?.querySelector('[data-target] button[aria-current="true"]');
        const currentId =
          activeThumb?.closest('[data-target]')?.dataset.target || activeMedia?.dataset.mediaId || '';

        const currentIndex = items.findIndex((item) => {
          const id = item.dataset.target || item.dataset.mediaId;
          return id === currentId;
        });
        const index = currentIndex >= 0 ? currentIndex : 0;
        const nextIndex = (index + direction + items.length) % items.length;
        const nextId = items[nextIndex].dataset.target || items[nextIndex].dataset.mediaId;
        if (nextId) this.setActiveMedia(nextId, false);
      }

      onSlideChanged(event) {
        const mediaId = event.detail?.currentElement?.dataset?.mediaId;
        if (!mediaId || !this.elements.thumbnails) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (this.closest('.nutra-pdp')) return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
